import { useState, useEffect, useMemo } from 'react';
import {
  Card, Row, Col, Select, Button, Table, Space, Tag, Alert,
  Spin, message, Tooltip, Typography, Divider
} from 'antd';
import {
  SearchOutlined, FilePdfOutlined, FileExcelOutlined,
  TableOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { getCicloActual, getCicloAcademico, getAniosAcademicos } from '../../../utils/cicloEscolar';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Option } = Select;
const { Text } = Typography;

const NOTA_APROBACION = 60;

// ── helpers ───────────────────────────────────────────────────────────────────
const colorNota = (val, max) => {
  if (val === null || val === undefined) return {};
  const pct = max > 0 ? (val / max) * 100 : 0;
  return pct >= NOTA_APROBACION
    ? { color: '#389e0d', fontWeight: 600 }
    : { color: '#cf1322', fontWeight: 600 };
};

const colorTotal = (val) => {
  if (val === null || val === undefined) return {};
  return val >= NOTA_APROBACION
    ? { color: '#389e0d', fontWeight: 700 }
    : { color: '#cf1322', fontWeight: 700 };
};

// ── Componente ────────────────────────────────────────────────────────────────
const ListadoCotejoCurso = ({ user }) => {
  const userData  = user || JSON.parse(localStorage.getItem('user') || '{}');
  const esAdmin   = userData.rol === 1;
  const esDocente = userData.rol === 4;

  // ── Catálogos (admin) ──────────────────────────────────────────────────
  const [grados,   setGrados]   = useState([]);
  const [secciones,setSecciones]= useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);

  // ── Filtros admin (el docente solo usa anio) ───────────────────────────
  // Admin: ciclo adelantado (reporte). Docente: ciclo académico, cambia en enero.
  const [anio,      setAnio]      = useState(esAdmin ? getCicloActual() : getCicloAcademico());
  const [idGrado,   setIdGrado]   = useState(null);
  const [idSeccion, setIdSeccion] = useState(null);
  const [idJornada, setIdJornada] = useState(null);

  // ── Asignaciones (cursos disponibles) ─────────────────────────────────
  const [asignaciones,       setAsignaciones]       = useState([]);
  const [loadingAsignaciones,setLoadingAsignaciones]= useState(false);

  // ── Filtro de grado client-side (docente) ──────────────────────────────
  const [gradoFiltroDocente, setGradoFiltroDocente] = useState(null);

  // ── Selección curso / unidad ───────────────────────────────────────────
  const [cursoSel,   setCursoSel]   = useState(null);
  const [unidades,   setUnidades]   = useState([]);
  const [loadingUnidades, setLoadingUnidades] = useState(false);
  const [unidadSel,  setUnidadSel]  = useState(null);

  // ── Resultado ──────────────────────────────────────────────────────────
  const [actividades, setActividades] = useState([]);
  const [alumnos,     setAlumnos]     = useState([]);
  const [loadingDatos,setLoadingDatos]= useState(false);
  const [buscado,     setBuscado]     = useState(false);

  // ── Init ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (esAdmin) cargarCatalogosAdmin();
  }, []);

  useEffect(() => {
    if (esDocente) cargarCursosDocente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anio]);

  // ── Admin: catálogos ───────────────────────────────────────────────────
  const cargarCatalogosAdmin = async () => {
    setLoadingCatalogos(true);
    try {
      const [g, s, j] = await Promise.all([
        apiClient.get('/grados'),
        apiClient.get('/secciones'),
        apiClient.get('/jornadas'),
      ]);
      if (g.data.success) setGrados(g.data.data);
      if (s.data.success) setSecciones(s.data.data);
      if (j.data.success) setJornadas(j.data.data);
    } catch {
      message.error('Error al cargar catálogos');
    } finally {
      setLoadingCatalogos(false);
    }
  };

  // ── Admin: buscar asignaciones con los filtros elegidos ────────────────
  const buscarAsignacionesAdmin = async () => {
    setLoadingAsignaciones(true);
    resetCursoEnAdelante();
    try {
      const params = { anio };
      if (idGrado)   params.idGrado   = idGrado;
      if (idSeccion) params.idSeccion = idSeccion;
      if (idJornada) params.idJornada = idJornada;
      const res = await apiClient.get('/asignaciones', { params });
      setAsignaciones(normalizarAsignaciones(res.data));
    } catch {
      message.error('Error al cargar asignaciones');
      setAsignaciones([]);
    } finally {
      setLoadingAsignaciones(false);
    }
  };

  // ── Docente: cargar sus propios cursos ─────────────────────────────────
  const cargarCursosDocente = async () => {
    setLoadingAsignaciones(true);
    try {
      const docentesRes = await apiClient.get('/docentes');
      if (!docentesRes.data.success) throw new Error();
      const miDocente = docentesRes.data.data.find(
        d => d.idUsuario === userData.IdUsuario
      );
      if (!miDocente) {
        message.error('No se encontró el perfil de docente asociado a tu usuario');
        return;
      }
      const asigRes = await apiClient.get('/asignaciones', {
        params: { idDocente: miDocente.idDocente, anio },
      });
      setAsignaciones(normalizarAsignaciones(asigRes.data));
    } catch {
      message.error('Error al cargar tus cursos');
    } finally {
      setLoadingAsignaciones(false);
    }
  };

  // ── Normalizar la respuesta de /asignaciones (puede venir en varios formatos) ──
  const normalizarAsignaciones = (responseData) => {
    if (responseData.success && responseData.data) {
      const data = responseData.data;
      if (Array.isArray(data)) {
        if (data.length > 0 && !data[0].IdAsignacionDocente) {
          return Object.keys(data[0])
            .filter(k => !isNaN(k))
            .map(k => data[0][k])
            .filter(i => i && i.IdAsignacionDocente);
        }
        return data.filter(i => i && i.IdAsignacionDocente);
      }
    }
    if (Array.isArray(responseData)) return responseData;
    return [];
  };

  // ── Grados únicos que tiene el docente (client-side) ──────────────────
  const gradosDocente = useMemo(() => {
    if (!esDocente) return [];
    const nombres = [...new Set(asignaciones.map(a => a.NombreGrado).filter(Boolean))];
    return nombres.sort();
  }, [asignaciones, esDocente]);

  // ── Cursos filtrados para el docente ──────────────────────────────────
  const asignacionesFiltradas = useMemo(() => {
    if (!esDocente || !gradoFiltroDocente) return asignaciones;
    return asignaciones.filter(a => a.NombreGrado === gradoFiltroDocente);
  }, [asignaciones, gradoFiltroDocente, esDocente]);

  // ── Cascada: curso seleccionado ────────────────────────────────────────
  const handleCursoChange = async (idAsignacion) => {
    setCursoSel(idAsignacion);
    setUnidadSel(null);
    setUnidades([]);
    setBuscado(false);
    setActividades([]);
    setAlumnos([]);
    setLoadingUnidades(true);
    try {
      const res = await apiClient.get(`/unidades/asignacion/${idAsignacion}`);
      setUnidades(res.data.data || res.data || []);
    } catch {
      message.error('Error al cargar unidades');
    } finally {
      setLoadingUnidades(false);
    }
  };

  // ── Buscar cuadro ──────────────────────────────────────────────────────
  const buscarCuadro = async () => {
    if (!cursoSel || !unidadSel) {
      message.warning('Seleccioná un curso y una unidad');
      return;
    }
    setLoadingDatos(true);
    setBuscado(false);
    setActividades([]);
    setAlumnos([]);
    try {
      const res = await apiClient.get('/boleta-calificaciones/cuadro-cotejo-curso', {
        params: { idAsignacionDocente: cursoSel, idUnidad: unidadSel },
      });
      if (res.data.success) {
        setActividades(res.data.data.actividades || []);
        setAlumnos(res.data.data.alumnos || []);
        setBuscado(true);
      } else {
        message.error('Error al obtener el cuadro de cotejo');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al conectar con el servidor');
    } finally {
      setLoadingDatos(false);
    }
  };

  // ── Resets ─────────────────────────────────────────────────────────────
  const resetCursoEnAdelante = () => {
    setCursoSel(null);
    setUnidades([]);
    setUnidadSel(null);
    setActividades([]);
    setAlumnos([]);
    setBuscado(false);
  };

  // ── Valores derivados ──────────────────────────────────────────────────
  const cursoActual  = asignaciones.find(a => a.IdAsignacionDocente === cursoSel);
  const unidadActual = unidades.find(u => u.IdUnidad === unidadSel);
  const hayDatos     = buscado && alumnos.length > 0;

  const etiquetaHeader = [
    cursoActual?.NombreCurso,
    cursoActual?.NombreGrado,
    cursoActual?.NombreSeccion ? `Sección ${cursoActual.NombreSeccion}` : '',
    cursoActual?.NombreJornada,
    unidadActual?.NombreUnidad,
  ].filter(Boolean).join(' · ');

  // ── Columnas dinámicas ─────────────────────────────────────────────────
  const columnas = [
    {
      title: '#', key: 'num', width: 44, fixed: 'left', align: 'center',
      render: (_, __, i) => i + 1,
    },
    {
      title: 'Alumno', key: 'alumno', width: 210, fixed: 'left',
      render: (_, r) => (
        <span style={{ fontWeight: 500 }}>{r.Apellidos}, {r.Nombres}</span>
      ),
    },
    // Una columna por actividad
    ...actividades.map((act, colIdx) => ({
      title: (
        <Tooltip title={`${act.NombreActividad} — ${act.TipoActividad === 'zona' ? 'Zona' : 'Final'}`}>
          <div style={{ fontSize: 11, lineHeight: '1.3' }}>
            {act.NombreActividad}
            <div>
              <Tag
                color={act.TipoActividad === 'zona' ? 'blue' : 'purple'}
                style={{ fontSize: 9, margin: '2px 0 0', padding: '0 4px' }}
              >
                {act.TipoActividad === 'zona' ? 'Zona' : 'Final'}
              </Tag>
              <Text type="secondary" style={{ fontSize: 10, marginLeft: 2 }}>
                /{act.PunteoMaximo}
              </Text>
            </div>
          </div>
        </Tooltip>
      ),
      key: `act_${act.idActividad}`,
      width: 88,
      align: 'center',
      render: (_, r) => {
        const nota = r.notasPorActividad[colIdx];
        const val  = nota?.Punteo ?? null;
        if (val === null) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return <span style={colorNota(val, act.PunteoMaximo)}>{val}</span>;
      },
    })),
    // Columna total unidad
    {
      title: (
        <Tooltip title="Nota total de la unidad (valor cacheado — puede no coincidir si se editaron notas sin recalcular)">
          <Space size={3}>
            Total unidad
            <InfoCircleOutlined style={{ color: '#faad14', fontSize: 11 }} />
          </Space>
        </Tooltip>
      ),
      key: 'totalUnidad', width: 105, align: 'center',
      render: (_, r) => {
        const val = r.totalUnidad;
        if (val === null || val === undefined)
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        return <span style={colorTotal(val)}>{val}</span>;
      },
    },
    // Promedio actual
    {
      title: (
        <Tooltip title="Promedio entre las unidades ya cerradas">
          <span>Prom. actual</span>
        </Tooltip>
      ),
      key: 'promedioActual', width: 95, align: 'center',
      render: (_, r) => {
        const val = r.promedioActual;
        if (val === null || val === undefined)
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        return <span style={colorTotal(val)}>{val}</span>;
      },
    },
    // Promedio acumulado
    {
      title: (
        <Tooltip title="Promedio acumulado ÷ 4 unidades (mismo criterio que la boleta)">
          <span>Prom. acumulado</span>
        </Tooltip>
      ),
      key: 'promedioAcumulado', width: 110, align: 'center',
      render: (_, r) => {
        const val = r.promedioAcumulado;
        if (val === null || val === undefined)
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        return <span style={colorTotal(val)}>{val}</span>;
      },
    },
  ];

  // ── Exportar PDF ───────────────────────────────────────────────────────
  const exportarPDF = () => {
    if (!hayDatos) { message.warning('No hay datos para exportar'); return; }
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 14;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('LISTA DE COTEJO POR CURSO', pageW / 2, y, { align: 'center' });
    y += 7;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(etiquetaHeader, pageW / 2, y, { align: 'center' });
    y += 5;

    const fecha = new Date().toLocaleDateString('es-GT', { day:'2-digit', month:'2-digit', year:'numeric' });
    doc.text(`Fecha de reporte: ${fecha}   ·   Docente: ${cursoActual?.NombreDocente || '—'}`, pageW / 2, y, { align: 'center' });
    y += 2;
    doc.setDrawColor(200);
    doc.line(14, y, pageW - 14, y);
    y += 4;

    const encabezados = [
      '#', 'Alumno',
      ...actividades.map(a => `${a.NombreActividad}\n(${a.TipoActividad === 'zona' ? 'Zona' : 'Final'} /${a.PunteoMaximo})`),
      'Total\nunidad', 'Prom.\nactual', 'Prom.\nacum.',
    ];

    const filas = alumnos.map((al, i) => [
      i + 1,
      `${al.Apellidos}, ${al.Nombres}`,
      ...al.notasPorActividad.map(n => n.Punteo !== null && n.Punteo !== undefined ? n.Punteo : '—'),
      al.totalUnidad     ?? '—',
      al.promedioActual  ?? '—',
      al.promedioAcumulado ?? '—',
    ]);

    autoTable(doc, {
      startY: y,
      head: [encabezados],
      body: filas,
      styles: { fontSize: 7, cellPadding: 2, halign: 'center' },
      headStyles: { fillColor: [24, 144, 255], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      columnStyles: { 0: { cellWidth: 8 }, 1: { halign: 'left', cellWidth: 48 } },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index >= 2) {
          const txt = data.cell.text[0];
          if (txt !== '—') {
            const n = Number(txt);
            if (!isNaN(n) && n >= 0) {
              data.cell.styles.textColor = n >= NOTA_APROBACION ? [40, 140, 40] : [180, 30, 30];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      },
    });

    const nombre = (cursoActual?.NombreCurso || 'cotejo').replace(/\s+/g, '_');
    const unidNom = (unidadActual?.NombreUnidad || '').replace(/\s+/g, '_');
    doc.save(`CotejoCurso_${nombre}_${unidNom}.pdf`);
    message.success('PDF generado correctamente');
  };

  // ── Exportar Excel ─────────────────────────────────────────────────────
  const exportarExcel = () => {
    if (!hayDatos) { message.warning('No hay datos para exportar'); return; }
    const fecha = new Date().toLocaleDateString('es-GT');

    const metaRows = [
      ['LISTA DE COTEJO POR CURSO'],
      [],
      ['Curso:',    cursoActual?.NombreCurso   || ''],
      ['Grado:',    cursoActual?.NombreGrado   || ''],
      ['Sección:',  cursoActual?.NombreSeccion || ''],
      ['Jornada:',  cursoActual?.NombreJornada || ''],
      ['Docente:',  cursoActual?.NombreDocente || ''],
      ['Unidad:',   unidadActual?.NombreUnidad || ''],
      ['Fecha:',    fecha],
      ['Total alumnos:', alumnos.length],
      [],
      [
        '#', 'Apellidos', 'Nombres',
        ...actividades.map(a => `${a.NombreActividad} (${a.TipoActividad}/${a.PunteoMaximo})`),
        'Total Unidad', 'Prom. Actual', 'Prom. Acumulado',
      ],
      ...alumnos.map((al, i) => [
        i + 1,
        al.Apellidos,
        al.Nombres,
        ...al.notasPorActividad.map(n => n.Punteo !== null && n.Punteo !== undefined ? n.Punteo : ''),
        al.totalUnidad     ?? '',
        al.promedioActual  ?? '',
        al.promedioAcumulado ?? '',
      ]),
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(metaRows);
    XLSX.utils.book_append_sheet(wb, ws, 'Cotejo');
    const nombre = (cursoActual?.NombreCurso || 'cotejo').replace(/\s+/g, '_');
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]),
      `CotejoCurso_${nombre}.xlsx`
    );
    message.success('Excel exportado correctamente');
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Space><TableOutlined />Lista de Cotejo por Curso</Space>}
        extra={
          hayDatos && (
            <Space>
              <Button icon={<FilePdfOutlined />} onClick={exportarPDF}>PDF</Button>
              <Button icon={<FileExcelOutlined />} onClick={exportarExcel}
                style={{ borderColor: '#52c41a', color: '#389e0d' }}>Excel</Button>
            </Space>
          )
        }
      >
        {/* ── FILTROS ADMIN ─────────────────────────────────────────────── */}
        {esAdmin && (
          <>
            <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
              <Col xs={24} sm={6} md={4} lg={3}>
                <Select value={anio} onChange={v => { setAnio(v); resetCursoEnAdelante(); setAsignaciones([]); }}
                  style={{ width: '100%' }}>
                  {getAniosAcademicos().map(y => <Option key={y} value={y}>{y}</Option>)}
                </Select>
              </Col>
              <Col xs={24} sm={8} md={6} lg={5}>
                <Select placeholder="Grado" style={{ width: '100%' }} value={idGrado}
                  onChange={v => { setIdGrado(v); resetCursoEnAdelante(); setAsignaciones([]); }}
                  loading={loadingCatalogos} allowClear showSearch optionFilterProp="children">
                  {grados.map(g => <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>)}
                </Select>
              </Col>
              <Col xs={24} sm={6} md={4} lg={3}>
                <Select placeholder="Sección" style={{ width: '100%' }} value={idSeccion}
                  onChange={v => { setIdSeccion(v); resetCursoEnAdelante(); setAsignaciones([]); }}
                  loading={loadingCatalogos} allowClear>
                  {secciones.map(s => <Option key={s.IdSeccion} value={s.IdSeccion}>{s.NombreSeccion}</Option>)}
                </Select>
              </Col>
              <Col xs={24} sm={6} md={5} lg={4}>
                <Select placeholder="Jornada" style={{ width: '100%' }} value={idJornada}
                  onChange={v => { setIdJornada(v); resetCursoEnAdelante(); setAsignaciones([]); }}
                  loading={loadingCatalogos} allowClear>
                  {jornadas.map(j => <Option key={j.IdJornada} value={j.IdJornada}>{j.NombreJornada}</Option>)}
                </Select>
              </Col>
              <Col xs={24} sm={6} md={4} lg={3}>
                <Button type="primary" icon={<SearchOutlined />} onClick={buscarAsignacionesAdmin}
                  loading={loadingAsignaciones} style={{ width: '100%' }}>
                  Buscar cursos
                </Button>
              </Col>
            </Row>
            <Divider style={{ margin: '4px 0 12px' }} />
          </>
        )}

        {/* ── FILTROS DOCENTE ───────────────────────────────────────────── */}
        {esDocente && (
          <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={6} md={4} lg={3}>
              <Select value={anio} style={{ width: '100%' }}
                onChange={v => { setAnio(v); setGradoFiltroDocente(null); resetCursoEnAdelante(); setAsignaciones([]); }}>
                {getAniosAcademicos().map(y => <Option key={y} value={y}>{y}</Option>)}
              </Select>
            </Col>
            {/* Filtro de grado (client-side) */}
            {gradosDocente.length > 1 && (
              <Col xs={24} sm={8} md={7} lg={5}>
                <Select placeholder="Filtrar por grado" style={{ width: '100%' }}
                  value={gradoFiltroDocente}
                  onChange={v => { setGradoFiltroDocente(v || null); resetCursoEnAdelante(); }}
                  allowClear loading={loadingAsignaciones}>
                  {gradosDocente.map(g => <Option key={g} value={g}>{g}</Option>)}
                </Select>
              </Col>
            )}
          </Row>
        )}

        {/* ── SELECTOR DE CURSO ─────────────────────────────────────────── */}
        <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={10} lg={9}>
            <Select
              placeholder={esAdmin ? 'Seleccioná un curso de los resultados' : 'Seleccioná tu curso'}
              style={{ width: '100%' }}
              value={cursoSel}
              onChange={handleCursoChange}
              loading={loadingAsignaciones}
              showSearch
              optionFilterProp="children"
              notFoundContent={
                loadingAsignaciones ? <Spin size="small" /> :
                esAdmin ? 'Buscá cursos primero' : 'Sin cursos disponibles'
              }
            >
              {(esDocente ? asignacionesFiltradas : asignaciones).map(a => (
                <Option key={a.IdAsignacionDocente} value={a.IdAsignacionDocente}>
                  {a.NombreCurso}
                  {esAdmin && a.NombreDocente &&
                    <Text type="secondary" style={{ fontSize: 11 }}> — {a.NombreDocente}</Text>}
                  {` · ${a.NombreGrado || ''}`}
                  {a.NombreSeccion ? ` ${a.NombreSeccion}` : ''}
                </Option>
              ))}
            </Select>
          </Col>

          {cursoSel && (
            <Col xs={24} sm={9} md={7} lg={6}>
              <Select
                placeholder="Seleccioná una unidad"
                style={{ width: '100%' }}
                value={unidadSel}
                onChange={v => { setUnidadSel(v); setBuscado(false); }}
                loading={loadingUnidades}
              >
                {unidades.map(u => (
                  <Option key={u.IdUnidad} value={u.IdUnidad}>
                    {u.NombreUnidad}
                    {(u.Cerrada || u.EstaCerrada)
                      ? <Tag color="red"   style={{ fontSize: 10, marginLeft: 6 }}>Cerrada</Tag>
                      : <Tag color="green" style={{ fontSize: 10, marginLeft: 6 }}>Abierta</Tag>}
                  </Option>
                ))}
              </Select>
            </Col>
          )}

          {cursoSel && unidadSel && (
            <Col xs={24} sm={5} md={4} lg={3}>
              <Button type="primary" icon={<SearchOutlined />} onClick={buscarCuadro}
                loading={loadingDatos} style={{ width: '100%' }}>
                Ver
              </Button>
            </Col>
          )}
        </Row>

        {/* ── INFO / LEYENDA ────────────────────────────────────────────── */}
        {hayDatos && (
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color="success">≥ 60 — Aprueba</Tag>
            <Tag color="error">&lt; 60 — Reprueba</Tag>
            <Tag color="default">— Sin nota</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {alumnos.length} alumno{alumnos.length !== 1 ? 's' : ''} · {actividades.length} actividad{actividades.length !== 1 ? 'es' : ''}
            </Text>
          </Space>
        )}

        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message={
            <span>
              <strong>Total unidad</strong>: valor guardado al momento del cierre — puede diferir de la suma
              visible si se editaron notas después sin recalcular.{' '}
              <strong>Prom. acumulado</strong>: suma de unidades cerradas ÷ 4 (mismo criterio que la boleta).
            </span>
          }
        />

        {/* ── TABLA ────────────────────────────────────────────────────── */}
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columnas}
            dataSource={alumnos}
            rowKey="IdAlumno"
            loading={loadingDatos}
            size="small"
            bordered
            pagination={{
              pageSize: 40,
              showSizeChanger: true,
              pageSizeOptions: ['20', '40', '80'],
              showTotal: t => `Total: ${t} alumnos`,
            }}
            scroll={{ x: 260 + actividades.length * 88 + 310 }}
            locale={{
              emptyText: buscado
                ? 'No hay alumnos o actividades en esta unidad'
                : 'Elegí un curso y una unidad, luego presioná Ver',
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ListadoCotejoCurso;
